<template>
  <pe-drawer v-model="isOpen" :title="`${$t('button.export')} ${t(`${titlePag}`) || $t('labels.table')}`" :width="580">
    <div class="flex flex-col h-full overflow-auto box-border text-neutral-900 dark:text-neutral-400 w-full">
      <div class="mt-2 mb-4 border-0 border-b border-solid border-b-neutral-300" />
      <div class="mb-8">{{ $t('mainContent.export.help') }}</div>
      <div class="mb-8 flex items-center gap-3">
        <div>
          {{ $t('mainContent.export.exportAll') }}
          <pe-tooltip :text="$t('tooltips.export')" icon/>
        </div>
        <label>
          <UiSwitch size="small" v-model="exportAll" /> {{ exportAll ? t('labels.yes') : t('labels.no') }}
        </label>
      </div>
      <UseMouseInElement v-slot="{ elementX, elementY }" class=" h-full">
        <div class="flex flex-row h-full gap-6 relative">
          <div class="w-1/2 h-full box-border">
            <div :title="`${t('mainContent.export.availableColumns')} ${leftCols.length}`" class="mb-2 flex justify-between truncate">
              {{ $t('mainContent.export.availableColumns') }} <span>{{ leftCols.length }}</span>
            </div>
            <div
              class="flex flex-col items-start box-border h-full rounded-xl border border-solid border-neutral-300 dark:border-neutral-500 bg-slate-200
              dark:bg-neutral-800 dark:text-neutral-100"
            >
              <div class="p-4 w-full box-border">
                <div class="mb-4">
                  <UiInput v-model="searchTerm" size="medium" :placeholder="$t('input.search')" class="w-full">
                    <template #suffix>
                      <UiIcon icon="search" class="text-neutral-500" size="20" />
                    </template>
                  </UiInput>
                </div>
                
                <div class="font-medium text-left cursor-pointer text-slate-500 dark:text-slate-100" @click="addAll()">
                  <pe-icon icon="content_paste_go" :size="24" /> {{ $t('mainContent.export.moveColumns') }}
                </div>
              </div>
              <div class="w-full border-0 border-b border-solid border-b-neutral-300 dark:border-b-neutral-500" />
              <div class="p-4 w-full h-full box-border flex flex-col gap-2 overflow-y-auto">
                <div
                  v-for="col in leftCols"
                  :key="col.prop"
                >
                  <UseMousePressed v-slot="{ pressed }">
                    <div
                      :key="col.prop"
                      class="w-full max-w-[220px] bg-white box-border flex justify-between items-center rounded dark:bg-neutral-600 dark:text-neutral-100"
                      :class="{ 'absolute pointer-events-none opacity-80 -translate-x-[200px]': pressed && dragColItem.item.prop }"
                      :style="pressed && dragColItem.item.prop ? `left: ${elementX}px; top: ${elementY}px` : ''"
                    >
                      <div class="p-2 grow">{{ col.name }}</div>
                      <div
                        class="h-full w-2/12 box-border grid place-items-center border-0 border-l border-solid border-l-slate-300 cursor-move" 
                        @click.stop="toExport.push(col)"
                      >
                        <pe-icon :weight="300" :size="20" icon="arrow_right_alt" class="text-slate-500 dark:text-neutral-100" />
                      </div>
                      <div
                        class="h-full w-2/12 box-border grid place-items-center border-0 border-l border-solid border-l-slate-300 cursor-move" 
                        @mousedown.prevent="startDragging(col)"
                      >
                        <pe-icon :size="24" icon="drag_indicator" class="text-slate-500 dark:text-neutral-100" />
                      </div>
                    </div>
                  </UseMousePressed>
                </div>
              </div>
            </div>
          </div>
          <div class="w-1/2 h-full box-border">
            <div :title="`${$t('mainContent.export.columnsExported')} ${toExport.length}`" class="mb-2 flex justify-between truncate">
              {{ $t('mainContent.export.columnsExported') }} <span>{{ toExport.length }}</span>
            </div>
            <div
              class="flex flex-col items-start box-border h-full rounded-xl border border-solid border-neutral-200 bg-neutral-100
              dark:bg-neutral-800 dark:border-neutral-500"
            >
              <div class="p-4 w-full box-border">
                <div class="font-medium text-center cursor-pointer text-slate-500 dark:text-slate-100" @click="() => toExport.splice(0, toExport.length)"><pe-icon icon="delete" :size="24" /> {{ $t('mainContent.export.removeColumns') }}</div>
              </div>
              <div class="w-full border-0 border-b border-solid border-b-neutral-300 dark:border-b-neutral-500" />
              <div class="p-4 w-full h-full box-border flex flex-col gap-2 overflow-y-auto" @mouseup="onRelease(true)">
                <div 
                  v-for="(col, index) in toExport" 
                  :key="index" 
                  class="w-full bg-white box-border flex justify-between items-center rounded dark:bg-neutral-600 dark:text-neutral-100"
                >
                  <div class="p-2">{{ col.name }}</div>
                  <div
                    class="h-full w-2/12 box-border grid place-items-center border-0 border-l border-solid border-l-slate-300 cursor-move" 
                    @click="toExport.splice(index, 1)"
                  >
                    <pe-icon :weight="300" :size="20" icon="arrow_right_alt" class="text-slate-500 rotate-180 dark:text-neutral-100" />
                  </div>
                </div>
                <div class="h-full max-h-full border-1 border-dashed border-slate-300 flex flex-col items-center justify-center">
                  <div class="text-center text-slate-400 dark:">
                    <pe-icon icon="place_item" :weight="200" :size="36" class="block mb-2" />
                    <div>{{ $t('mainContent.export.dragColumns') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UseMouseInElement>
      <div class="py-4 grow mt-6 flex justify-end items-end space-x-2">
        <UiButton type="danger" outline class="px-6 py-3 text-sm uppercase" @click="isOpen = false">
          {{ $t('button.cancel') }}
        </UiButton>
        <UiButton type="primary" class="px-6 py-3 text-sm uppercase" :disabled="!toExport.length" @click="exportData()">
          {{ $t('button.export') }}
        </UiButton>
      </div>
    </div>
  </pe-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { UseMousePressed, UseMouseInElement } from '@vueuse/components'
import { useEventListener } from '@vueuse/core'
import { utils, writeFileXLSX } from 'xlsx'
import UiButton from '@/components/ui/button.vue'
import UiSwitch from '@/components/ui/switch.vue'
import UiInput from '@/components/ui/input.vue'
import UiIcon from '@/components/ui/icon.vue'

import { formatter } from '@/common/util'

import type { TableColumnsType } from '@/types/types'

interface ColItem {
  prop: string
  name: string
  transform?: string
}

const props = defineProps<{
  modelValue?: boolean
  cols: ColItem[]
  tableData?: any[]
  allData?: any[]
  defaultCols?: TableColumnsType[]
  filters?: any
}>()

const { t } = useI18n()
const route = useRoute()

const isOpen = defineModel({
  default: false
})

const filterSelected = computed(() => props.filters || {})

watch(isOpen, (value) => value && setDefaultColumns())

const searchTerm = ref('')
const toExport = ref<ColItem[]>([])
const exportAll = ref(true)

const leftCols = computed(() => props.cols.filter((c) => !toExport.value.map((exp) => exp.prop).includes(c.prop)).filter(c => c.name.toLocaleLowerCase().indexOf(searchTerm.value.toLocaleLowerCase()) > -1))
const titlePag = computed(() => route.meta?.title as string)
const hasFilter = computed(() => Object.values(filterSelected).some((el: any) => typeof el === 'boolean' ? true : el?.length))

const dragColItem = ref<{
  status: number
  item: ColItem
}>({
  status: 0,
  item: {
    prop: '',
    name: ''
  }
})

const getFormatter = {
  'active-indicator': (value: boolean) => (value ? t('labels.active') : t('labels.inactive')),
  'type-people': formatter.typePeople,
  'format-cpfcnpj': formatter.cpfCnpj,
  'format-date': formatter.date,
  'format-status': (value: boolean) => (value ? t('labels.active') : t('labels.inactive')),
  'format-boolean': (value: boolean) => (value ? t('labels.yes') : t('labels.no')),
  'status-percentage': (value: number) => (value === 0 ? 'Inscrito' : `${value}% concluído`),
  'type-classificacao-cliente': formatter.typeClassificacaoCliente,
  'type-produto-aliare': formatter.typeProdutoAliare
}

const setDefaultColumns = () => {
  // Função que insere colunas já exibidas na tela para exportar
  // Como a coluna id não é exibida é necessário a concatenação
  const columnsData = props.defaultCols?.map((el) => el.prop) || props.cols.map((el) => el.prop)
  toExport.value = [{name: t('column.identifier'), prop: 'id'}].concat(props.cols.filter((el) => columnsData.includes(el.prop)))

  toExport.value = toExport.value.filter((col, index, self) =>
    index === self.findIndex((t) => (
      t.prop === col.prop
    ))
  )
}

const startDragging = (col: ColItem) => {
  dragColItem.value.status = 1
  dragColItem.value.item = col
}

const onRelease = (isValid?: boolean) => {
  if (dragColItem.value.item.prop && isValid)
    toExport.value.push(dragColItem.value.item)

  dragColItem.value.status = 0
  dragColItem.value.item = {
    prop: '',
    name: ''
  }
}

const addAll = () => {
  toExport.value.splice(0, toExport.value.length)

  props.cols.forEach(col => {
    toExport.value.push(col)
  })
}

const exportXlsx = (data) => {
  const date = new Date().toLocaleDateString('pt-br').split('/').join('')
  const time = new Date().toLocaleTimeString('pt-br').split(':').join('')

  const fileName = `${t(titlePag.value)}_${date}_${time}.xlsx`

  const wb = utils.book_new()
  utils.book_append_sheet(wb, utils.json_to_sheet(data), 'Dados Distribuidor')
  writeFileXLSX(wb, fileName)
}

const exportData = () => {
  const rows = exportAll.value ? props.allData : props.tableData
  const exportkeys = toExport.value.map((el) => el.prop)
  const headers = {}
  const filters = []

  toExport.value.forEach((el, i) => {
    headers[el.prop] = {
      name: el.name, 
      order: i,
      transform: getFormatter[el.transform] || null 
    }
    
    if (hasFilter.value && filterSelected.value?.[el.prop])
      filters.push(el.name)
  })

  const data = (rows || []).map((row) => {
    return Object.keys(row)
      .filter((key) => exportkeys.includes(key))
      .sort((a, b) => headers[a].order < headers[b].order ? -1 : 1)
      .reduce((obj, key) => {
        obj[`${headers[key].name}`] = headers[key].transform ? headers[key].transform(row[key]) : row[key]
        return obj
      }, {})
  })

  filters.map((el, i) => {
    data[i] 
      ? data[i][t('labels.filter', 2)] = el
      : data.push({[t('labels.filter', 2)]: el})
  })


  exportXlsx(data)
}

useEventListener('mouseup', () => {
  onRelease()
})

defineExpose({
  toggle: () => isOpen.value = !isOpen.value,
  open: () => isOpen.value = true,
  close: () => isOpen.value = false
})
</script>

<style scoped>
*::-webkit-scrollbar {
  width: 7px;
}

*::-webkit-scrollbar-track {
  background: transparent;
}

*::-webkit-scrollbar-thumb {
  background-color: #a8a8a8;
  border-radius: 4px;
}
</style>
